import React, { useState, useEffect } from 'react';
import { uploadImage } from '../firebase';
import { db } from '../firebase';
import { buildEventFields, fetchEvents } from '../utils/events';

const ProductForm = ({ product, onSubmit, onCancel, categories, setCategories }) => {
  const initialCategories = Array.isArray(product?.categories) && product.categories.length
    ? product.categories
    : (product?.category ? [product.category] : []);
  const [model, setModel] = useState(product?.model || '');
  const [price, setPrice] = useState(product?.price || '');
  const [description, setDescription] = useState(product?.description || '');
  const [selectedCategories, setSelectedCategories] = useState(initialCategories);
  const [newCategory, setNewCategory] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState(product?.images || []);
  const [loading, setLoading] = useState(false);
  const [stock, setStock] = useState(product?.stock || 0);
  const [hidden, setHidden] = useState(Boolean(product?.hidden));
  const [discountExcluded, setDiscountExcluded] = useState(product?.discountExcluded ?? false);
  const [excludeFromStock, setExcludeFromStock] = useState(product?.excludeFromStock ?? false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(product?.eventId || '');

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setEvents(await fetchEvents(db));
      } catch (error) {
        console.error('Error loading events:', error);
      }
    };

    loadEvents();
  }, []);

  useEffect(() => {
    if (files.length > 0) {
      const fileReaders = [];
      files.forEach((file, index) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews(prevPreviews => {
            const newPreviews = [...prevPreviews];
            newPreviews[index] = reader.result;
            return newPreviews;
          });
        };
        reader.readAsDataURL(file);
        fileReaders.push(reader);
      });
    }
  }, [files]);

  const handleRemoveImage = (index) => {
    setPreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleCategoryAdd = (categoryToAdd) => {
    if (!categoryToAdd) return;
    setSelectedCategories(prev => (
      prev.includes(categoryToAdd) ? prev : [...prev, categoryToAdd]
    ));
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(prev => prev.filter(cat => cat !== categoryToRemove));
  };

  const handleNewCategoryAdd = () => {
    const trimmedCategory = newCategory.trim();
    if (!trimmedCategory) return;

    handleCategoryAdd(trimmedCategory);
    if (!categories.includes(trimmedCategory)) {
      setCategories([...categories, trimmedCategory].sort((a, b) => a.localeCompare(b)));
    }
    setNewCategory('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let imageUrls = [];
    if (files.length > 0) {
      try {
        const uploadPromises = files.map((file) => uploadImage(file));
        imageUrls = await Promise.all(uploadPromises);
      } catch (error) {
        console.error('Error uploading images to Storage:', error);
        alert(error.message || 'Error al subir imágenes. Intenta de nuevo.');
        setLoading(false);
        return;
      }
    } else {
      imageUrls = previews;
    }

    const trimmedNewCategory = newCategory.trim();
    const finalCategories = [...selectedCategories, trimmedNewCategory]
      .filter(Boolean)
      .filter((cat, index, arr) => arr.indexOf(cat) === index);
    const finalCategory = finalCategories[0] || '';

    if (!finalCategory) {
      alert('Seleccione o cree al menos una categoría.');
      setLoading(false);
      return;
    }

    if (trimmedNewCategory && !categories.includes(trimmedNewCategory)) {
      setCategories([...categories, trimmedNewCategory].sort((a, b) => a.localeCompare(b)));
    }

    const selectedEvent = events.find(event => event.id === selectedEventId) || null;

    onSubmit({
      model,
      images: imageUrls,
      price: parseFloat(price),
      description,
      category: finalCategory,
      categories: finalCategories,
      stock: parseInt(stock),
      hidden,
      discountExcluded,
      excludeFromStock,
      ...buildEventFields(selectedEvent)
    });
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="product-form-container">
      <h3>{product ? 'Editar Producto' : 'Agregar Nuevo Producto'}</h3>
      <div className="form-group-container">
        <label htmlFor="model" className="form-label">Modelo</label>
        <input
          type="text"
          id="model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="images" className="form-label">Imágenes (hasta 3)</label>
        <input type="file" onChange={(e) => setFiles([...files, ...e.target.files])} accept=".jpg,.png" multiple className="form-input" />
        <div className="image-preview-container">
          {previews.map((preview, index) => (
            <div key={index} className="image-preview-wrapper">
              <img src={preview} alt={`Preview ${index + 1}`} className="image-preview" />
              <button type="button" onClick={() => handleRemoveImage(index)} className="remove-image-button">x</button>
            </div>
          ))}
        </div>
      </div>
      <div className="form-group-container">
        <label htmlFor="price" className="form-label">Precio</label>
        <input
          type="number"
          id="price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="stock" className="form-label">Stock</label>
        <input
          type="number"
          id="stock"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          required
          className="form-input"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="description" className="form-label">Descripción</label>
        <textarea
          id="description"
          maxLength={300}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          className="form-textarea"
        />
      </div>
      <div className="form-group-container">
        <label htmlFor="eventId" className="form-label">Evento asociado</label>
        <select
          id="eventId"
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          className="form-select"
        >
          <option value="">Sin evento asignado</option>
          {events.map(event => (
            <option key={event.id} value={event.id}>
              {event.name}{event.eventDate ? ` - ${event.eventDate}` : ''}{event.isActive ? ' (activo)' : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group-container">
        <label htmlFor="category" className="form-label">Categorías</label>
        {selectedCategories.length > 0 && (
          <div className="product-form-category-chips">
            {selectedCategories.map(cat => (
              <span key={cat} className="product-form-category-chip">
                {cat}
                <button
                  type="button"
                  className="product-form-category-chip-remove"
                  onClick={() => handleCategoryRemove(cat)}
                  title="Quitar categoría"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <select
          id="category"
          value=""
          onChange={(e) => {
            handleCategoryAdd(e.target.value);
            e.target.value = '';
          }}
          className="form-select"
        >
          <option value="">Agregar categoría...</option>
          {categories.filter(cat => !selectedCategories.includes(cat)).map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>
      <div className="form-group-container">
        <label htmlFor="newCategory" className="form-label">Nueva Categoría</label>
        <div className="product-form-inline-group">
          <input
            type="text"
            id="newCategory"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="form-input"
          />
          <button type="button" onClick={handleNewCategoryAdd} className="form-action-button submit">Añadir</button>
        </div>
      </div>
      <div className="form-group-container product-form-flags">
        <label className={`product-form-flag ${hidden ? 'is-negative' : ''}`}>
          <input
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
          />
          <span>Producto oculto</span>
        </label>
        <label className={`product-form-flag ${discountExcluded ? 'is-negative' : ''}`}>
          <input
            type="checkbox"
            checked={!discountExcluded}
            onChange={(e) => setDiscountExcluded(!e.target.checked)}
          />
          <span>Apto descuento</span>
        </label>
        <label className={`product-form-flag ${excludeFromStock ? 'is-negative' : ''}`}>
          <input
            type="checkbox"
            checked={!excludeFromStock}
            onChange={(e) => setExcludeFromStock(!e.target.checked)}
          />
          <span>Computa stock</span>
        </label>
      </div>
      <div className="form-actions-container">
        <button type="submit" disabled={loading} className="form-action-button submit">
          Guardar
        </button>
        <button type="button" onClick={onCancel} disabled={loading} className="form-action-button cancel">Cancelar</button>
      </div>
    </form>
  );
};

export default ProductForm;
